import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import {
  InventoryPackItem,
  InventoryPackItemInput,
  InventoryPackSummary,
  InventoryVariant,
} from "../../../../../../models/inventory/InventoryAdmin";

interface EditablePackItem {
  clientSku: string;
  productId: number | null;
  productName: string | null;
  qty: string;
}

interface EditPackItemsPopupProps {
  open: boolean;
  pack: InventoryPackSummary | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSearchVariants: (search: string) => Promise<InventoryVariant[]>;
  onLoadPackItems?: (packId: number) => Promise<InventoryPackItem[]>;
  onSubmit: (items: InventoryPackItemInput[]) => void | Promise<void>;
}

export default function EditPackItemsPopup({
  open,
  pack,
  isSubmitting = false,
  onClose,
  onSearchVariants,
  onLoadPackItems,
  onSubmit,
}: EditPackItemsPopupProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [items, setItems] = useState<EditablePackItem[]>([]);
  const [variantOptions, setVariantOptions] = useState<FilterValue[]>([]);
  const [variantsBySku, setVariantsBySku] = useState<
    Record<string, InventoryVariant>
  >({});
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const existingSkusRef = useRef<Set<string>>(new Set());
  const isSubmitLockedRef = useRef(false);

  const submitInProgress = isSubmitting || isSubmitLocked;

  useEffect(() => {
    if (!open || !pack) return;

    let cancelled = false;
    isSubmitLockedRef.current = false;
    setIsSubmitLocked(false);

    const seedFromPack = (packItems: InventoryPackItem[]) => {
      if (cancelled) return;
      setItems(
        packItems.map((item) => ({
          clientSku: item.clientSku?.trim() || "",
          productId: item.productId ?? null,
          productName: item.productName ?? null,
          qty: String(item.qty ?? 1),
        })),
      );
    };

    seedFromPack(pack.items || []);
    setVariantOptions([]);
    setVariantsBySku({});
    setSearchError(null);

    if ((pack.items?.length || 0) > 0 || !onLoadPackItems) {
      return () => {
        cancelled = true;
      };
    }

    setIsLoadingItems(true);
    onLoadPackItems(pack.packId)
      .then((loadedItems) => {
        seedFromPack(loadedItems);
      })
      .catch(() => {
        if (!cancelled) setSearchError("Failed to load pack items");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingItems(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pack?.packId]);

  const existingSkus = useMemo(
    () => new Set(items.map((item) => item.clientSku.trim()).filter(Boolean)),
    [items],
  );

  useEffect(() => {
    existingSkusRef.current = existingSkus;
  }, [existingSkus]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const parsedItems = items.map((item) => ({
    clientSku: item.clientSku.trim(),
    qty: Number(item.qty || "0"),
  }));

  const isValid =
    parsedItems.length > 0 &&
    parsedItems.every(
      (item) =>
        item.clientSku.length > 0 &&
        Number.isFinite(item.qty) &&
        Number.isInteger(item.qty) &&
        item.qty >= 1,
    );

  const fieldsDisabled = submitInProgress || isLoadingItems;

  const handleClose = () => {
    if (submitInProgress) return;
    onClose();
  };

  const handleQtyChange = (index: number, value: string) => {
    if (fieldsDisabled) return;
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, qty: value } : item,
      ),
    );
  };

  const handleRemoveItem = (index: number) => {
    if (fieldsDisabled) return;
    setItems((current) =>
      current.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const runSearch = async (searchText: string) => {
    try {
      setSearchError(null);
      const variants = await onSearchVariants(searchText || "");
      const nextBySku: Record<string, InventoryVariant> = {};
      const options: FilterValue[] = [];
      const blockedSkus = existingSkusRef.current;

      variants.forEach((variant) => {
        const sku = variant.clientSku?.trim();
        if (!sku || blockedSkus.has(sku)) return;
        nextBySku[sku] = variant;
        options.push({
          id: sku,
          value: variant.productName
            ? `${variant.productName} (${sku})`
            : sku,
        });
      });

      setVariantsBySku(nextBySku);
      setVariantOptions(options);
    } catch (error: any) {
      setSearchError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to search SKUs",
      );
      setVariantOptions([]);
    }
  };

  const handleSearch = (searchText?: string) => {
    if (fieldsDisabled) return;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      void runSearch(searchText || "");
    }, 300);
  };

  const handleSelectVariant = (selected: FilterValue[]) => {
    if (fieldsDisabled) return;
    const selectedOption = selected[0];
    if (!selectedOption) return;

    const sku = String(selectedOption.id).trim();
    if (!sku || existingSkus.has(sku)) return;

    const variant = variantsBySku[sku];
    setItems((current) => [
      ...current,
      {
        clientSku: sku,
        productId: variant?.productId ?? null,
        productName: variant?.productName || null,
        qty: "1",
      },
    ]);
    setVariantOptions((current) =>
      current.filter((option) => String(option.id) !== sku),
    );
  };

  const handleSubmit = async () => {
    try {
      if (!isValid || isSubmitLockedRef.current || submitInProgress) return;
      isSubmitLockedRef.current = true;
      setIsSubmitLocked(true);
      await onSubmit(parsedItems);
    } catch (error) {
      console.error("Failed to save pack items:", error);
    } finally {
      isSubmitLockedRef.current = false;
      setIsSubmitLocked(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: "16px" },
            overflow: "hidden",
            boxShadow: {
              xs: "none",
              sm: "0 20px 40px rgba(0, 0, 0, 0.15)",
            },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            height: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "90vh" },
            width: "100%",
            m: { xs: 0, sm: 2 },
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: { xs: "stretch", sm: "center" },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          maxHeight: { xs: "100%", sm: "90vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            flexShrink: 0,
          }}
        >
          <Box minWidth={0}>
            <CustomTypography variant="heading4" fontWeight={700}>
              Edit Pack Items
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Replace BOM items for {pack?.label || "this pack"}
            </CustomTypography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={submitInProgress}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: { xs: 1.5, sm: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, flexShrink: 0 }}>
            <CustomTypography variant="body2" fontWeight={600}>
              ADD SKU
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Search inventory variants by SKU or product name
            </CustomTypography>
            <Box sx={{ mt: 1.5 }}>
              <AppSearchDropdownMulti
                isMultiSelect={false}
                title="Search SKU"
                list={variantOptions}
                value={[]}
                handleSearch={handleSearch}
                handleSelect={handleSelectVariant}
                onOpen={() => {
                  if (fieldsDisabled) return;
                  void runSearch("");
                }}
                disabled={fieldsDisabled}
                fontSize="12px"
                minWidth="100%"
              />
            </Box>
            {searchError && (
              <CustomTypography variant="body3" color="error" sx={{ mt: 1 }}>
                {searchError}
              </CustomTypography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CustomTypography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
              BOM ITEMS ({items.length})
            </CustomTypography>
            {isLoadingItems ? (
              <CustomTypography
                variant="body3"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Loading items…
              </CustomTypography>
            ) : items.length === 0 ? (
              <CustomTypography
                variant="body3"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Add at least one SKU to save the BOM.
              </CustomTypography>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                gap={1.5}
                sx={{
                  mt: 1.5,
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  pr: 0.5,
                  maxHeight: { xs: "none", sm: "min(48vh, 420px)" },
                }}
              >
                {items.map((item, index) => (
                  <Box
                    key={`${item.clientSku || "empty"}-${item.productId}-${index}`}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.palette.customColors.white,
                      border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    }}
                  >
                    <Grid2 container spacing={1.5} alignItems="center">
                      <Grid2 size={{ xs: 12, sm: "grow" }} minWidth={0}>
                        <CustomTypography variant="body2" fontWeight={600}>
                          {item.productName ||
                            (item.productId != null
                              ? `Product ID: ${item.productId}`
                              : "—")}
                        </CustomTypography>
                        {item.clientSku ? (
                          <Box sx={{ mt: 0.25 }}>
                            <SkuCopyRow
                              sku={item.clientSku}
                              fontWeight={600}
                            />
                          </Box>
                        ) : (
                          <CustomTypography variant="caption" color="error">
                            SKU required — remove or replace this row
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: "auto" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: { xs: "100%", sm: "auto" },
                          }}
                        >
                          <Box sx={{ width: { xs: "100%", sm: 110 } }}>
                            <CustomTextField
                              type="number"
                              value={item.qty}
                              onChange={(e) =>
                                handleQtyChange(index, e.target.value)
                              }
                              width="100%"
                              placeholder="Qty"
                              allowNegativeValue={false}
                              allowDecimalValue={false}
                              disabled={fieldsDisabled || !item.clientSku}
                            />
                          </Box>
                          <IconButton
                            onClick={() => handleRemoveItem(index)}
                            size="small"
                            disabled={fieldsDisabled}
                            aria-label="Remove item"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid2>
                    </Grid2>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1.5}
            flexWrap="wrap"
            sx={{
              pt: 1,
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <AppButton
              label="Cancel"
              onClick={handleClose}
              variant="grayscale"
              disabled={submitInProgress}
              customStyles={{ width: { xs: "100%", sm: "auto" } }}
            />
            <AppButton
              label={submitInProgress ? "Saving..." : "Save Items"}
              onClick={() => {
                void handleSubmit();
              }}
              variant="primary"
              disabled={!isValid || submitInProgress}
              customStyles={{
                width: { xs: "100%", sm: "auto" },
                pointerEvents: submitInProgress ? "none" : "auto",
              }}
            />
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
